// SubscriptionBoxes.js — monthly subscription box planner (brand-scoped, currently Suzanne Somers)
//
// Concept: each month is its own box record with its own internal SKU (e.g. SUB-JUN26), pointing
// at a single parent product (the Shopify PDP customers actually order). Components in this
// month's box, multiplied by the box's forecast, drive component demand. "Ending early" zeros
// the remaining forecast. Component health rolls up across all active boxes.
//
// Run this SQL in Supabase before using this page:
//
// create table subscription_boxes (
//   id uuid primary key default gen_random_uuid(),
//   brand_id uuid references brands(id) not null,
//   name text not null,
//   sku text not null,
//   parent_product_id uuid references products(id) not null,
//   start_date date not null,
//   end_date date not null,
//   forecast_qty integer,
//   ended_early_at timestamptz,
//   created_by uuid references user_profiles(id),
//   created_at timestamptz default now()
// );
// create unique index subscription_boxes_brand_sku_idx on subscription_boxes(brand_id, sku);
// alter table subscription_boxes enable row level security;
// create policy "brand_access" on subscription_boxes
//   using (brand_id in (select brand_id from user_brand_access where user_id = auth.uid()));
//
// create table subscription_box_items (
//   id uuid primary key default gen_random_uuid(),
//   box_id uuid references subscription_boxes(id) on delete cascade not null,
//   product_id uuid references products(id) not null,
//   quantity integer not null default 1,
//   unique (box_id, product_id)
// );
// alter table subscription_box_items enable row level security;
// create policy "brand_access" on subscription_box_items
//   using (box_id in (select id from subscription_boxes where brand_id in (
//     select brand_id from user_brand_access where user_id = auth.uid()
//   )));

import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Plus, X, AlertTriangle, Package, Edit2, Trash2, StopCircle, Calendar } from 'lucide-react'
import { format, differenceInCalendarDays, parseISO } from 'date-fns'
import ProductPicker from '../components/ProductPicker'

const DAILY_VELOCITY_LOOKBACK_DAYS = 28

function todayISO() {
  return new Date().toISOString().split('T')[0]
}

function parseDate(d) {
  return parseISO(d + 'T12:00:00')
}

function formatDate(d) {
  return d ? format(parseDate(d), 'MMM d, yyyy') : '—'
}

function boxStatus(box) {
  if (box.ended_early_at) return 'ended_early'
  const today = todayISO()
  if (today < box.start_date) return 'upcoming'
  if (today > box.end_date) return 'completed'
  return 'active'
}

const STATUS_LABEL = {
  active: 'Active',
  upcoming: 'Upcoming',
  completed: 'Completed',
  ended_early: 'Ended early'
}

const STATUS_BADGE = {
  active: 'success',
  upcoming: 'info',
  completed: 'neutral',
  ended_early: 'warning'
}

export default function SubscriptionBoxes() {
  const { activeBrand, user } = useAuth()
  const [boxes, setBoxes] = useState([])
  const [items, setItems] = useState([])
  const [products, setProducts] = useState([])
  const [onHand, setOnHand] = useState([])
  const [inboundByProduct, setInboundByProduct] = useState({}) // { product_id: [{ shipment_number, eta, qty, status }] }
  const [standaloneRateByProduct, setStandaloneRateByProduct] = useState({}) // { product_id: shopify_units_28d / 28 }
  const [salesByParent, setSalesByParent] = useState({}) // { parent_product_id: { [yyyy-mm-dd]: qty, total28: n } }
  const [loading, setLoading] = useState(true)
  const [tableError, setTableError] = useState(false)
  const [statusFilter, setStatusFilter] = useState('active')

  // Edit modal
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState(null) // box being edited, or null for new
  const [form, setForm] = useState(emptyForm())
  const [formItems, setFormItems] = useState([]) // [{ product_id, quantity }]
  const [productPicker, setProductPicker] = useState('')
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  function emptyForm() {
    return { name: '', sku: '', parent_product_id: '', start_date: '', end_date: '', forecast_qty: '' }
  }

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)

    // Fetch boxes first so we know which parent products to pull sales for
    const { data: boxesData, error: boxesError } = await supabase
      .from('subscription_boxes')
      .select('*, parent_product:parent_product_id(id, sku, name)')
      .eq('brand_id', activeBrand.id)
      .order('start_date', { ascending: false })

    if (boxesError && (boxesError.code === '42P01' || boxesError.message?.includes('does not exist'))) {
      setTableError(true)
      setLoading(false)
      return
    }

    const [
      { data: itemsData },
      { data: prodsData },
      { data: ohData },
      { data: shipData },
      { data: strData }
    ] = await Promise.all([
      supabase.from('subscription_box_items')
        .select('*')
        .in('box_id', (boxesData || []).map(b => b.id).length ? (boxesData || []).map(b => b.id) : ['00000000-0000-0000-0000-000000000000']),
      supabase.from('products')
        .select('id, sku, name, product_type')
        .eq('brand_id', activeBrand.id)
        .is('deleted_at', null)
        .order('sku'),
      supabase.from('inventory_on_hand')
        .select('product_id, on_hand')
        .eq('brand_id', activeBrand.id),
      supabase.from('shipments')
        .select('id, shipment_number, expected_arrival, status, shipment_line_items(product_id, quantity_ordered, quantity_received)')
        .eq('brand_id', activeBrand.id)
        .is('deleted_at', null)
        .not('status', 'in', '("received","cancelled")'),
      supabase.from('sell_through_rates')
        .select('product_id, shopify_units_28d')
        .eq('brand_id', activeBrand.id)
    ])

    const standaloneMap = {}
    for (const r of strData || []) {
      const v = Number(r.shopify_units_28d || 0)
      if (v > 0) standaloneMap[r.product_id] = v / 28
    }

    // Roll up open shipment lines into per-product inbound list (sorted by ETA asc)
    const inboundMap = {}
    for (const ship of shipData || []) {
      for (const line of ship.shipment_line_items || []) {
        if (!line.product_id) continue
        const remaining = Number(line.quantity_ordered || 0) - Number(line.quantity_received || 0)
        if (remaining <= 0) continue
        if (!inboundMap[line.product_id]) inboundMap[line.product_id] = []
        inboundMap[line.product_id].push({
          shipment_id: ship.id,
          shipment_number: ship.shipment_number,
          eta: ship.expected_arrival,
          status: ship.status,
          qty: remaining
        })
      }
    }
    for (const pid of Object.keys(inboundMap)) {
      inboundMap[pid].sort((a, b) => {
        if (!a.eta) return 1
        if (!b.eta) return -1
        return a.eta.localeCompare(b.eta)
      })
    }

    // Pull sell-thru for parent products: last DAILY_VELOCITY_LOOKBACK_DAYS days + within any active/upcoming box window
    const parentIds = [...new Set((boxesData || []).map(b => b.parent_product_id))]
    const salesMap = {}
    if (parentIds.length > 0) {
      const lookbackStart = new Date(Date.now() - DAILY_VELOCITY_LOOKBACK_DAYS * 86400000).toISOString()
      const { data: lineItems } = await supabase
        .from('shopify_order_line_items')
        .select('product_id, quantity, shopify_order_id, shopify_orders!inner(created_at_shopify, brand_id)')
        .eq('brand_id', activeBrand.id)
        .in('product_id', parentIds)
        .gte('shopify_orders.created_at_shopify', lookbackStart)
        .limit(50000)

      for (const li of lineItems || []) {
        const pid = li.product_id
        const day = (li.shopify_orders?.created_at_shopify || '').split('T')[0]
        if (!day) continue
        if (!salesMap[pid]) salesMap[pid] = { byDay: {}, total: 0 }
        salesMap[pid].byDay[day] = (salesMap[pid].byDay[day] || 0) + Number(li.quantity || 0)
        salesMap[pid].total += Number(li.quantity || 0)
      }
    }

    setBoxes(boxesData || [])
    setItems(itemsData || [])
    setProducts(prodsData || [])
    setOnHand(ohData || [])
    setInboundByProduct(inboundMap)
    setStandaloneRateByProduct(standaloneMap)
    setSalesByParent(salesMap)
    setTableError(false)
    setLoading(false)
  }

  // ── Helpers ──
  const productById = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products])

  function getItemsForBox(boxId) {
    return items.filter(i => i.box_id === boxId)
  }

  function getOnHandTotal(productId) {
    return onHand
      .filter(r => r.product_id === productId)
      .reduce((sum, r) => sum + Number(r.on_hand || 0), 0)
  }

  // Sales of the parent SKU during the given box's window (from start_date to min(today, end_date))
  function getSoldSoFar(box) {
    const sales = salesByParent[box.parent_product_id]
    if (!sales) return 0
    const start = box.start_date
    const today = todayISO()
    const end = box.end_date < today ? box.end_date : today
    let total = 0
    for (const [day, qty] of Object.entries(sales.byDay)) {
      if (day >= start && day <= end) total += qty
    }
    return total
  }

  function getBoxMetrics(box) {
    const status = boxStatus(box)
    const forecast = Number(box.forecast_qty || 0)
    const sold = getSoldSoFar(box)
    let remaining
    if (status === 'ended_early' || status === 'completed') {
      remaining = 0
    } else if (status === 'upcoming') {
      remaining = forecast
    } else {
      remaining = Math.max(0, forecast - sold)
    }
    const today = todayISO()
    const daysLeft = status === 'active'
      ? Math.max(1, differenceInCalendarDays(parseDate(box.end_date), parseDate(today)) + 1)
      : status === 'upcoming'
        ? differenceInCalendarDays(parseDate(box.end_date), parseDate(box.start_date)) + 1
        : 0
    const dailyRemaining = daysLeft > 0 ? remaining / daysLeft : 0
    return { status, forecast, sold, remaining, daysLeft, dailyRemaining }
  }

  // Component health rollup across active + upcoming boxes
  const componentHealth = useMemo(() => {
    const liveBoxes = boxes.filter(b => {
      const s = boxStatus(b)
      return s === 'active' || s === 'upcoming'
    })

    // componentId -> { committed, boxRate, boxes: [{ box, qty, contribution }] }
    const byComponent = {}
    for (const box of liveBoxes) {
      const metrics = getBoxMetrics(box)
      const boxItems = getItemsForBox(box.id)
      for (const item of boxItems) {
        if (!byComponent[item.product_id]) {
          byComponent[item.product_id] = { committed: 0, boxRate: 0, boxes: [] }
        }
        const ref = byComponent[item.product_id]
        const qtyPerBox = Number(item.quantity || 1)
        ref.committed += metrics.remaining * qtyPerBox
        ref.boxRate += metrics.dailyRemaining * qtyPerBox
        ref.boxes.push({ box, qty: qtyPerBox, contribution: metrics.remaining * qtyPerBox, daily: metrics.dailyRemaining * qtyPerBox })
      }
    }

    return Object.entries(byComponent).map(([productId, agg]) => {
      const product = productById[productId]
      const oh = getOnHandTotal(productId)
      const inboundList = inboundByProduct[productId] || []
      const inboundQty = inboundList.reduce((s, x) => s + x.qty, 0)
      const earliestEta = inboundList.find(x => x.eta)?.eta || null
      const standaloneRate = standaloneRateByProduct[productId] || 0
      const totalRate = agg.boxRate + standaloneRate
      const daysOfCover = totalRate > 0 ? oh / totalRate : Infinity
      const shortfall = Math.max(0, agg.committed - oh)
      const inboundCovers = shortfall > 0 && inboundQty >= shortfall

      let status = 'ok'
      if (oh + inboundQty < agg.committed) status = 'sold_out'
      else if (oh < agg.committed && inboundCovers) status = 'needs_inbound'
      else if (daysOfCover < 7) status = 'at_risk'

      return {
        productId, product, onHand: oh, inboundQty, inboundList, earliestEta,
        committed: agg.committed, shortfall,
        boxRate: agg.boxRate, standaloneRate, totalRate, daysOfCover,
        status, boxes: agg.boxes
      }
    }).sort((a, b) => {
      const rank = { sold_out: 0, needs_inbound: 1, at_risk: 2, ok: 3 }
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status]
      return a.daysOfCover - b.daysOfCover
    })
  }, [boxes, items, products, onHand, inboundByProduct, standaloneRateByProduct, salesByParent, productById])

  // ── Filter rows ──
  const visibleBoxes = useMemo(() => {
    if (statusFilter === 'all') return boxes
    return boxes.filter(b => boxStatus(b) === statusFilter)
  }, [boxes, statusFilter])

  // ── Edit/create flow ──
  function openNew() {
    setEditing(null)
    setForm(emptyForm())
    setFormItems([])
    setProductPicker('')
    setFormError('')
    setModal(true)
  }

  function openEdit(box) {
    setEditing(box)
    setForm({
      name: box.name || '',
      sku: box.sku || '',
      parent_product_id: box.parent_product_id,
      start_date: box.start_date,
      end_date: box.end_date,
      forecast_qty: box.forecast_qty?.toString() || ''
    })
    setFormItems(getItemsForBox(box.id).map(i => ({ product_id: i.product_id, quantity: i.quantity })))
    setProductPicker('')
    setFormError('')
    setModal(true)
  }

  function suggestedSkuFromDate(d) {
    if (!d) return ''
    const dt = parseDate(d)
    return 'SUB-' + format(dt, 'MMMyy').toUpperCase()
  }

  function autoFillSkuIfBlank(startDate) {
    if (form.sku) return
    const suggested = suggestedSkuFromDate(startDate)
    if (suggested) setForm(f => ({ ...f, sku: suggested }))
  }

  function addItem(productId) {
    if (!productId) return
    if (formItems.some(i => i.product_id === productId)) return
    setFormItems(arr => [...arr, { product_id: productId, quantity: 1 }])
    setProductPicker('')
  }

  function updateItemQty(productId, qty) {
    setFormItems(arr => arr.map(i => i.product_id === productId ? { ...i, quantity: Math.max(1, parseInt(qty) || 1) } : i))
  }

  function removeItem(productId) {
    setFormItems(arr => arr.filter(i => i.product_id !== productId))
  }

  async function saveBox() {
    setFormError('')
    if (!form.name || !form.sku || !form.parent_product_id || !form.start_date || !form.end_date) {
      setFormError('Name, SKU, parent product, start, and end are required.')
      return
    }
    if (form.start_date > form.end_date) {
      setFormError('End date must be on or after start date.')
      return
    }
    if (formItems.length === 0) {
      setFormError('Add at least one product to the box.')
      return
    }
    setSaving(true)

    const payload = {
      brand_id: activeBrand.id,
      name: form.name,
      sku: form.sku,
      parent_product_id: form.parent_product_id,
      start_date: form.start_date,
      end_date: form.end_date,
      forecast_qty: form.forecast_qty ? parseInt(form.forecast_qty) : null
    }

    let boxId
    if (editing) {
      const { error } = await supabase.from('subscription_boxes').update(payload).eq('id', editing.id)
      if (error) { setFormError('Save failed: ' + error.message); setSaving(false); return }
      boxId = editing.id
      // Replace items
      await supabase.from('subscription_box_items').delete().eq('box_id', boxId)
    } else {
      const { data, error } = await supabase
        .from('subscription_boxes')
        .insert({ ...payload, created_by: user?.id })
        .select()
        .single()
      if (error) { setFormError('Save failed: ' + error.message); setSaving(false); return }
      boxId = data.id
    }

    const itemRows = formItems.map(i => ({ box_id: boxId, product_id: i.product_id, quantity: i.quantity }))
    const { error: itemsErr } = await supabase.from('subscription_box_items').insert(itemRows)
    if (itemsErr) { setFormError('Saved box but items failed: ' + itemsErr.message); setSaving(false); return }

    setSaving(false)
    setModal(false)
    load()
  }

  async function endEarly(box) {
    if (!window.confirm(`End "${box.name}" early? Remaining forecast will zero out and components will be released.`)) return
    await supabase.from('subscription_boxes').update({ ended_early_at: new Date().toISOString() }).eq('id', box.id)
    load()
  }

  async function deleteBox(box) {
    if (!window.confirm(`Delete "${box.name}"? This cannot be undone.`)) return
    await supabase.from('subscription_box_items').delete().eq('box_id', box.id)
    await supabase.from('subscription_boxes').delete().eq('id', box.id)
    load()
  }

  // ── Render ──
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  // Eligible parent products: anything in catalog. We don't filter type — the parent is whatever
  // SKU customers buy on the Shopify PDP.
  const addedProductIds = formItems.map(i => i.product_id)

  return (
    <div className="page">
      <div className="page-header">
        <h1>Subscription Boxes</h1>
        <div className="page-header-actions">
          {!tableError && (
            <button className="btn btn-primary" onClick={openNew}>
              <Plus size={14} />New Box
            </button>
          )}
        </div>
      </div>

      {/* Setup required */}
      {tableError && (
        <div className="card" style={{ borderColor: 'var(--warning)' }}>
          <div style={{ padding: '4px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
              <strong style={{ fontSize: '14px' }}>One-time setup required</strong>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '12px' }}>
              Run this SQL in your <strong>Supabase SQL editor</strong> to create the subscription box tables, then refresh:
            </p>
            <pre style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '14px', borderRadius: 'var(--radius)', fontSize: '12px', lineHeight: 1.7, overflow: 'auto', margin: 0 }}>{`create table subscription_boxes (
  id uuid primary key default gen_random_uuid(),
  brand_id uuid references brands(id) not null,
  name text not null,
  sku text not null,
  parent_product_id uuid references products(id) not null,
  start_date date not null,
  end_date date not null,
  forecast_qty integer,
  ended_early_at timestamptz,
  created_by uuid references user_profiles(id),
  created_at timestamptz default now()
);
create unique index subscription_boxes_brand_sku_idx on subscription_boxes(brand_id, sku);
alter table subscription_boxes enable row level security;
create policy "brand_access" on subscription_boxes
  using (brand_id in (select brand_id from user_brand_access where user_id = auth.uid()));

create table subscription_box_items (
  id uuid primary key default gen_random_uuid(),
  box_id uuid references subscription_boxes(id) on delete cascade not null,
  product_id uuid references products(id) not null,
  quantity integer not null default 1,
  unique (box_id, product_id)
);
alter table subscription_box_items enable row level security;
create policy "brand_access" on subscription_box_items
  using (box_id in (select id from subscription_boxes where brand_id in (
    select brand_id from user_brand_access where user_id = auth.uid()
  )));`}</pre>
          </div>
        </div>
      )}

      {!tableError && (
        <>
          {/* Status tabs */}
          <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>
            {[['active', 'Active'], ['upcoming', 'Upcoming'], ['completed', 'Completed'], ['ended_early', 'Ended early'], ['all', 'All']].map(([val, label]) => {
              const count = val === 'all' ? boxes.length : boxes.filter(b => boxStatus(b) === val).length
              return (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${statusFilter === val ? 'var(--accent)' : 'transparent'}`, color: statusFilter === val ? 'var(--accent)' : 'var(--text-3)', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginBottom: '-1px' }}
                >
                  {label}
                  {count > 0 && val !== 'all' && (
                    <span style={{ marginLeft: '6px', background: val === 'active' ? 'var(--success)' : 'var(--bg-4)', color: val === 'active' ? 'white' : 'var(--text-2)', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 700 }}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Boxes table */}
          <div className="card">
            {visibleBoxes.length === 0 ? (
              <div className="empty-state">
                <Calendar size={40} />
                <p>No subscription boxes {statusFilter !== 'all' ? `in "${STATUS_LABEL[statusFilter] || statusFilter}"` : 'yet'}</p>
                {boxes.length === 0 && (
                  <p style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '4px' }}>Click "New Box" to plan your first month.</p>
                )}
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>SKU</th>
                      <th>Parent</th>
                      <th>Window</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Forecast</th>
                      <th style={{ textAlign: 'right' }}>Sold</th>
                      <th style={{ textAlign: 'right' }}>Remaining</th>
                      <th>Products</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleBoxes.map(box => {
                      const m = getBoxMetrics(box)
                      const boxItems = getItemsForBox(box.id)
                      return (
                        <tr key={box.id}>
                          <td style={{ fontWeight: 500 }}>{box.name}</td>
                          <td><span className="mono" style={{ fontSize: '12px' }}>{box.sku}</span></td>
                          <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                            <span className="mono" style={{ fontSize: '11px', color: 'var(--text-3)', marginRight: '6px' }}>{box.parent_product?.sku}</span>
                            {box.parent_product?.name}
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                            {formatDate(box.start_date)} → {formatDate(box.end_date)}
                          </td>
                          <td><span className={`badge badge-${STATUS_BADGE[m.status]}`} style={{ fontSize: '10px' }}>{STATUS_LABEL[m.status]}</span></td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{box.forecast_qty?.toLocaleString() || '—'}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{m.status === 'upcoming' ? '—' : m.sold.toLocaleString()}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: m.remaining > 0 ? 'var(--text)' : 'var(--text-3)' }}>{m.remaining.toLocaleString()}</td>
                          <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>{boxItems.length}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => openEdit(box)} title="Edit"><Edit2 size={13} /></button>
                              {m.status === 'active' && (
                                <button className="btn btn-ghost btn-sm" onClick={() => endEarly(box)} title="End early">
                                  <StopCircle size={13} style={{ color: 'var(--warning)' }} />
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" onClick={() => deleteBox(box)} title="Delete">
                                <Trash2 size={13} style={{ color: 'var(--danger)' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Component health */}
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 4px 0' }}>Component Health</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '0 0 12px 0' }}>
              Rolled up across all active + upcoming boxes. Box rate = remaining forecast ÷ days remaining. Standalone rate = component's own Shopify sales (28-day avg). Days of cover uses total rate.
            </p>
            <div className="card">
              {componentHealth.length === 0 ? (
                <div className="empty-state">
                  <Package size={36} />
                  <p>No active or upcoming boxes to track.</p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Component</th>
                        <th style={{ textAlign: 'right' }}>On hand</th>
                        <th style={{ textAlign: 'right' }}>Inbound</th>
                        <th style={{ textAlign: 'right' }}>Committed</th>
                        <th style={{ textAlign: 'right' }} title="Daily demand from active + upcoming boxes">Box rate</th>
                        <th style={{ textAlign: 'right' }} title="Standalone Shopify sales over last 28 days ÷ 28">Standalone rate</th>
                        <th style={{ textAlign: 'right' }}>Total rate</th>
                        <th style={{ textAlign: 'right' }}>Days of cover</th>
                        <th>Status</th>
                        <th>In boxes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {componentHealth.map(row => {
                        const badgeKind =
                          row.status === 'sold_out' ? 'danger' :
                          row.status === 'needs_inbound' ? 'warning' :
                          row.status === 'at_risk' ? 'warning' : 'success'
                        const badgeLabel =
                          row.status === 'sold_out' ? 'Sold out' :
                          row.status === 'needs_inbound' ? 'Needs inbound' :
                          row.status === 'at_risk' ? 'At risk' : 'OK'
                        return (
                          <tr key={row.productId}>
                            <td>
                              <span className="mono" style={{ fontSize: '11px', color: 'var(--text-3)', marginRight: '6px' }}>{row.product?.sku || '?'}</span>
                              <span style={{ color: 'var(--text-2)' }}>{row.product?.name || '—'}</span>
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{row.onHand.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', color: row.inboundQty > 0 ? (row.status === 'needs_inbound' ? 'var(--warning)' : 'var(--text-2)') : 'var(--text-3)' }}>
                              {row.inboundQty > 0 ? (
                                <div title={row.inboundList.map(s => `${s.shipment_number}: ${s.qty} ${s.eta ? '· ' + formatDate(s.eta) : '· no ETA'} (${s.status})`).join('\n')}>
                                  <div style={{ fontWeight: 600 }}>+{row.inboundQty.toLocaleString()}</div>
                                  <div style={{ fontSize: '10px', color: 'var(--text-3)' }}>{row.earliestEta ? `ETA ${formatDate(row.earliestEta)}` : 'no ETA'}</div>
                                </div>
                              ) : '—'}
                            </td>
                            <td style={{ textAlign: 'right', color: row.onHand < row.committed ? 'var(--danger)' : 'var(--text-2)' }}>
                              {Math.round(row.committed).toLocaleString()}
                              {row.status === 'sold_out' && row.shortfall > row.inboundQty && (
                                <div style={{ fontSize: '10px', color: 'var(--danger)', fontWeight: 600 }}>
                                  short {Math.round(row.shortfall - row.inboundQty).toLocaleString()}
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{row.boxRate > 0 ? row.boxRate.toFixed(1) : '—'}</td>
                            <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{row.standaloneRate > 0 ? row.standaloneRate.toFixed(1) : '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>{row.totalRate > 0 ? row.totalRate.toFixed(1) : '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: row.daysOfCover < 7 ? 'var(--danger)' : row.daysOfCover < 14 ? 'var(--warning)' : 'var(--text-2)' }}>
                              {row.totalRate > 0 ? (isFinite(row.daysOfCover) ? row.daysOfCover.toFixed(0) : '∞') : '—'}
                            </td>
                            <td>
                              <span className={`badge badge-${badgeKind}`} style={{ fontSize: '10px' }}>{badgeLabel}</span>
                            </td>
                            <td style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                              {row.boxes.map(b => `${b.box.sku}${b.qty > 1 ? ` (×${b.qty})` : ''}`).join(', ')}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{editing ? `Edit: ${editing.name}` : 'New Subscription Box'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {formError && <div className="alert alert-danger">{formError}</div>}

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Box name *</label>
                  <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="June 2026 Selects Box" />
                </div>
                <div className="form-group">
                  <label className="form-label">SKU *</label>
                  <input className="input" value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} placeholder="SUB-JUN26" />
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>Auto-fills when you set a start date.</div>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Start date *</label>
                  <input className="input" type="date" value={form.start_date}
                    onChange={e => { setForm(f => ({ ...f, start_date: e.target.value })); autoFillSkuIfBlank(e.target.value) }} />
                </div>
                <div className="form-group">
                  <label className="form-label">End date *</label>
                  <input className="input" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Parent product (Shopify PDP) *</label>
                <ProductPicker
                  products={products}
                  value={form.parent_product_id}
                  onChange={id => setForm(f => ({ ...f, parent_product_id: id }))}
                  placeholder="Search for the subscription parent product..."
                />
                <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>The product customers actually buy on Shopify. Sell-thru is measured against this SKU during the box's window.</div>
              </div>

              <div className="form-group">
                <label className="form-label">Forecast quantity</label>
                <input className="input" type="number" min="0" value={form.forecast_qty} onChange={e => setForm(f => ({ ...f, forecast_qty: e.target.value }))} placeholder="e.g. 3000" />
                <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>How many of this box do you expect to ship? Used to compute component demand.</div>
              </div>

              {/* Products in box */}
              <div style={{ background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                  Products in this box
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <ProductPicker
                    products={products}
                    value={productPicker}
                    onChange={setProductPicker}
                    disabledIds={addedProductIds}
                    disabledLabel="added"
                    placeholder="Search to add a product..."
                    style={{ flex: 1 }}
                  />
                  <button className="btn btn-secondary btn-sm" onClick={() => addItem(productPicker)} disabled={!productPicker}>
                    <Plus size={13} />Add
                  </button>
                </div>
                {formItems.length === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>No products added yet.</div>
                ) : (
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                        <th style={{ textAlign: 'left', paddingBottom: '6px', fontWeight: 500 }}>Product</th>
                        <th style={{ textAlign: 'right', paddingBottom: '6px', fontWeight: 500, width: '120px' }}>Qty per box</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formItems.map(i => {
                        const p = productById[i.product_id]
                        return (
                          <tr key={i.product_id} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={{ padding: '6px 0' }}>
                              <span className="mono" style={{ fontSize: '11px', color: 'var(--text-3)' }}>{p?.sku || '?'}</span>
                              <span style={{ color: 'var(--text-2)', marginLeft: '8px' }}>{p?.name || '—'}</span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <input
                                className="input"
                                type="number"
                                min="1"
                                value={i.quantity}
                                onChange={e => updateItemQty(i.product_id, e.target.value)}
                                style={{ width: '90px', textAlign: 'right', padding: '4px 8px', fontSize: '12px' }}
                              />
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => removeItem(i.product_id)} title="Remove">
                                <X size={13} style={{ color: 'var(--danger)' }} />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveBox} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save changes' : 'Create box'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
